import { type RefObject } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, NavLink, useLocation } from 'react-router'
import { marketingNavLinks } from '#app/components/marketing-nav-links.ts'
import { Button } from '#app/components/ui/button.tsx'
import { Icon } from '#app/components/ui/icon.tsx'
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetOverlay,
	SheetTitle,
} from '#app/components/ui/sheet.tsx'
import { UserDropdown } from '#app/components/user-dropdown.tsx'
import { ThemeSwitch } from '#app/routes/resources/theme-switch.tsx'
import { isShopPath, useLocalizedPath } from '#app/utils/i18n.ts'
import { type Theme } from '#app/utils/theme.server.ts'
import { trackEvent } from '#app/utils/tracking.ts'

export function MobileMenu({
	isOpen,
	onClose,
	returnFocusRef,
	user,
	userPreference,
}: {
	isOpen: boolean
	onClose: () => void
	/**
	 * Gets focus back once the menu has closed. Radix only does this for a
	 * `SheetTrigger`, and the header's menu button sits outside the Sheet.
	 */
	returnFocusRef?: RefObject<HTMLElement | null>
	user: { name: string | null; username: string } | null
	userPreference?: Theme | null
}) {
	const { t } = useTranslation()
	const localePath = useLocalizedPath()
	const shopActive = isShopPath(useLocation().pathname)

	const navLinks = marketingNavLinks(localePath, t)

	return (
		<Sheet
			open={isOpen}
			onOpenChange={(open) => {
				if (!open) onClose()
			}}
		>
			<SheetOverlay className="md:hidden" />
			<SheetContent
				aria-describedby={undefined}
				className="block md:hidden"
				onCloseAutoFocus={(event) => {
					event.preventDefault()
					returnFocusRef?.current?.focus()
				}}
			>
				{/* No i18n key exists for this label yet; kept as the literal. */}
				<SheetTitle className="sr-only">Navigation</SheetTitle>

				{/* Close button */}
				<div className="flex justify-end">
					<SheetClose asChild>
						<Button
							variant="ghost"
							size="icon"
							aria-label={t('a11y.closeMenu')}
						>
							<Icon name="cross-1" size="md" />
						</Button>
					</SheetClose>
				</div>

				{/* Nav links */}
				<nav className="mt-6 flex flex-col gap-1">
					{navLinks.map((link) =>
						link.external ? (
							<a
								key={link.to}
								href={link.to}
								onClick={() => {
									trackEvent(
										'navigation',
										'click',
										`${link.slug}-mobile`,
									)
									onClose()
								}}
								aria-current={shopActive ? 'page' : undefined}
								className={`rounded-xl px-3 py-2 text-body-sm transition-colors ${
									shopActive
										? 'bg-accent font-semibold text-foreground'
										: 'text-muted-foreground hover:bg-accent hover:text-foreground'
								}`}
							>
								{link.label}
							</a>
						) : (
							<NavLink
								key={link.to}
								to={link.to}
								onClick={() => {
									trackEvent(
										'navigation',
										'click',
										`${link.slug}-mobile`,
									)
									onClose()
								}}
								className={({ isActive }) =>
									`rounded-xl px-3 py-2 text-body-sm transition-colors ${
										isActive
											? 'bg-accent font-semibold text-foreground'
											: 'text-muted-foreground hover:bg-accent hover:text-foreground'
									}`
								}
							>
								{link.label}
							</NavLink>
						),
					)}
				</nav>

				{/* Divider */}
				<div className="my-6 border-t border-border" />

				{/* Theme switch */}
				<div className="flex items-center gap-3 px-3">
					<span className="text-body-xs text-muted-foreground">
						{t('theme.label')}
					</span>
					<ThemeSwitch userPreference={userPreference} />
				</div>

				{/* Divider */}
				<div className="my-6 border-t border-border" />

				{/* Contact CTA */}
				<div className="px-3">
					<Button asChild variant="default" className="w-full">
						<Link to={localePath('/contact')}>
							{t('nav.contact')}
						</Link>
					</Button>
				</div>

				{/* User section */}
				{user ? (
					<div className="mt-4 px-3">
						<UserDropdown />
					</div>
				) : null}
			</SheetContent>
		</Sheet>
	)
}
